import { useState, useEffect, useCallback } from "react"
import PubSub from "pubsub-js"
import { PlusIcon } from "@heroicons/react/24/outline"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import Typography from "aldoo-ra/Typography"
import { openGoPremium } from "../GoPremiumDialog"
import { ListThemes } from "../FileShareFeature/api"
import ThemeThumbnail from "./ThemeCard"
import ThemeEditorDialog from "./ThemeEditorDialog"
import {
  VIRTUAL_DEFAULT_THEME, DEFAULT_THEME_ID,
  VIRTUAL_ACCOUNT_THEME, ACCOUNT_DEFAULT_ID,
} from "./constants"
import { showMessageDialog } from "aldoo-ra/MessageDialog"
import TextBundle from "aldoo-ra/CMS/text-bundle"

// Sentinel option id for the "+ Create new theme" row inside the dropdown.
// Selecting it opens the editor (or the GoPremium prompt for free users) and
// does NOT propagate as a real theme selection.
const CREATE_OPTION_ID = "__create__"

// Renders one row in the dropdown — thumbnail + name, with a special look for
// the "Create new theme" sentinel. Used as DropdownSelect's renderOption.
function ThemeRow({ option, texts }) {
  if (option._id === CREATE_OPTION_ID) {
    return (
      <div className="flex items-center gap-3 py-1.5">
        <span className="w-14 h-9 shrink-0 rounded-lg border-2 border-dashed border-primary/50 flex items-center justify-center text-primary">
          <PlusIcon className="w-5 h-5" />
        </span>
        <span className="text-sm font-medium text-primary">{texts?.create_theme_btn}</span>
      </div>
    )
  }
  return (
    <div className="flex items-center gap-3 py-1.5 min-w-0 overflow-hidden">
      <div className="w-10 sm:w-14 shrink-0">
        <ThemeThumbnail theme={option} />
      </div>
      <span className="block flex-1 text-xs sm:text-sm text-text truncate min-w-0">
        {option.content?.name || texts?.system_default}
      </span>
    </div>
  )
}

// Trigger renderer — same row layout as the menu rows so the selected theme
// reads identically to its option entry.
function ThemeTrigger({ option, texts }) {
  if (!option) return <span className="text-sm text-text_secondary">{texts?.select_theme_ph}</span>
  return <ThemeRow option={option} texts={texts} />
}

// Custom theme picker for the file-share options + drawer. DropdownSelect-
// backed so it gets free search, keyboard nav, and portal-positioned menu.
// Options: System Default first, then the user's owned themes, then a "+
// Create new theme" sentinel that opens the editor on click.
export default function ThemeSelect({ value, onChange }) {
  const [themes, setThemes] = useState([])
  const [canCustomize, setCanCustomize] = useState(false)

  const texts = TextBundle({ id: "transfer_theme_editor" })
  // Cap from the server's feature-lock resolver (ListThemes response).
  const [maxThemes, setMaxThemes] = useState(50)
  const [showEditor, setShowEditor] = useState(false)

  const load = useCallback(async (selectId) => {
    const res = await ListThemes().catch(() => null)
    const list = res?.themes || []
    setThemes(list)
    setCanCustomize(!!res?.canCustomize)
    if (Number.isFinite(res?.maxThemes)) setMaxThemes(res.maxThemes)
    // Only auto-fire onChange when the caller explicitly asked (selectId set
    // — e.g. after creating a new theme in the editor). Auto-selecting on
    // mount also fired a save in the share-drawer's Theme tab even when the
    // user hadn't picked anything, which masked real clicks downstream.
    if (selectId) onChange?.(selectId)
  }, [onChange])

  useEffect(() => { load() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Realtime: refetch the option list when themes change elsewhere.
  useEffect(() => {
    const token = PubSub.subscribe("transfer.theme.changed", () => load())
    return () => PubSub.unsubscribe(token)
  }, [load])

  // Order: Create → Account Default (live pointer to gallery default) →
  // System Default (built-in look) → user themes. The Account Default
  // row inherits its thumbnail/palette from whichever theme the user has
  // marked is_default — so the dropdown previews the actual look it'll
  // produce, not a generic placeholder.
  const accountDefaultSource = themes.find((t) => t.content?.is_default)
  const sourceName = accountDefaultSource?.content?.name || VIRTUAL_DEFAULT_THEME.content.name
  const accountDefaultOption = {
    ...VIRTUAL_ACCOUNT_THEME,
    content: {
      ...(accountDefaultSource?.content || VIRTUAL_DEFAULT_THEME.content),
      name: `${VIRTUAL_ACCOUNT_THEME.content.name} - ${sourceName}`,
    },
  }
  // DropdownSelect reads `option[nameField]` flat — dotted paths like
  // "content.name" don't resolve, which broke search. Spread a top-level
  // `name` onto every option so the search filter matches.
  const decorate = (o) => ({ ...o, name: o.content?.name || "" })
  const options = [
    decorate({ _id: CREATE_OPTION_ID, content: { name: texts?.create_theme_btn } }),
    decorate(accountDefaultOption),
    decorate(VIRTUAL_DEFAULT_THEME),
    ...themes.map(decorate),
  ]

  // DropdownSelect emits the picked array; pull the first entry. The create
  // sentinel is intercepted here so it never bubbles up as the share's theme.
  const handleChange = (selected) => {
    const picked = Array.isArray(selected) ? selected[0] : selected
    if (!picked) return
    if (picked._id === CREATE_OPTION_ID) {
      if (!canCustomize) { openGoPremium(); return }
      if (themes.length >= maxThemes) {
        showMessageDialog({
          title: texts?.theme_limit_title,
          message: texts?.theme_limit_msg,
        })
        return
      }
      setShowEditor(true)
      return
    }
    onChange?.(picked._id)
  }

  const onSaved = (theme) => {
    setShowEditor(false)
    if (theme?._id) load(theme._id)
  }

  // DropdownSelect expects an array of option objects. Account Default is
  // the implicit fallback (never Create) — new shares start in account-mode
  // by default and "no explicit value" should read as that.
  const valueArr = (() => {
    const match = options.find((o) => o._id === value && o._id !== CREATE_OPTION_ID)
    return match ? [match] : [VIRTUAL_ACCOUNT_THEME]
  })()

  return (
    <>
      <DropdownSelect
        options={options}
        value={valueArr}
        valueField="_id"
        nameField="name"
        onChange={handleChange}
        renderOption={(opt) => <ThemeRow option={opt} texts={texts} />}
        onValueItemRender={(opt) => <ThemeTrigger option={opt} texts={texts} />}
        searchEnabled
        showCheckbox={false}
        showChevron
        showSelectAll={false}
        enableResetButton={false}
        placeholder={texts?.select_theme_ph}
      />
      {showEditor ? (
        <ThemeEditorDialog theme={null} onClose={() => setShowEditor(false)} onSaved={onSaved} />
      ) : null}
    </>
  )
}
